import { formatPrimaryLink } from "fleet/view/device/table-format";
import { formatDate } from "fleet/view/incidents/table-format";
import PropTypes from "prop-types";
import React from "react";
import {
  Button,
  FormLabel,
  FormControl,
  FormGroup,
  FormText,
  Modal,
} from "react-bootstrap";
import * as Users from "redux-store/users/selectors";
import { Collapsible } from "ui/collapsible/collapsible";
import { NavLoader } from "ui/loader";
import { Table, Column } from "ui/table";
import { getErrorMessage } from "util/response";
import { utcToLocal } from "util/time";

class IncidentsTable extends React.PureComponent {
  static get propTypes() {
    return {
      condensed: PropTypes.bool,
      resolveMsgHandler: PropTypes.func,
      resolveIncident: PropTypes.func.isRequired,
      showDeviceNames: PropTypes.bool,
      sizePerPage: PropTypes.number,
      sizePerPageList: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string.isRequired,
          value: PropTypes.number.isRequired,
        }),
      ),
      storesLoaded: PropTypes.bool.isRequired,
      tableData: PropTypes.array.isRequired,
      users: PropTypes.object.isRequired,
    };
  }

  static get defaultProps() {
    return {
      condensed: false,
      resolveMsgHandler: () => {},
      showDeviceNames: false,
      sizePerPage: 10,
      sizePerPageList: null,
    };
  }

  constructor(props) {
    super(props);

    this._baseModalState = Object.freeze({
      comment: "",
      incidentId: null,
    });

    this.state = {
      ...this._baseModalState,
    };

    this.options = {
      defaultSortName: "create_date",
      defaultSortOrder: "desc",
      sizePerPage: props.sizePerPage,
    };

    if (props.sizePerPageList) {
      this.options.sizePerPageList = props.sizePerPageList;
    }

    this._formSubmit = this._formSubmit.bind(this);
    this._formatUsers = this._formatUsers.bind(this);
    this._formatIsResolved = this._formatIsResolved.bind(this);
    this._formatResolveDetails = this._formatResolveDetails.bind(this);
    this._handleCommentChange = this._handleCommentChange.bind(this);
    this._resetModalState = this._resetModalState.bind(this);
    this._submitErrorHandler = this._submitErrorHandler.bind(this);
    this._submitSuccessHandler = this._submitSuccessHandler.bind(this);
  }

  _formatIsResolved(isResolved, row) {
    if (isResolved) {
      return (
        <div className="text-green">
          <i className="fa fa-check" />
        </div>
      );
    }

    return (
      <Button
        variant="primary"
        className="resolve-button"
        onClick={() => this.setState({ incidentId: row.id })}
        size="sm"
      >
        Resolve
      </Button>
    );
  }

  _formatResolveDetails(_, row) {
    const {
      comment,
      resolved,
      resolve_date: resolveDate,
      user_id: resolvedByUser,
    } = row;

    if (!resolved) {
      return null;
    }

    const { users } = this.props;
    const user = users[resolvedByUser];
    const resolvedByElem = Users.toString(user, null);

    return (
      <div className="resolve-details">
        Resolved by {resolvedByElem || "unknown"} on
        {" " + utcToLocal(resolveDate)}.
        <br />
        <span className="resolve-message text-muted">{comment}</span>
      </div>
    );
  }

  _formatUsers(userIds) {
    const { users: usersById } = this.props;
    const users = userIds
      .map((userId, i) => {
        const user = usersById[userId];

        return user ? (
          <div key={i} className="user list-content">
            {Users.toString(user, null)}
          </div>
        ) : null;
      })
      .filter((elem) => elem);

    const usersLen = users.length;

    if (usersLen === 1) {
      return users[0];
    }

    return <Collapsible label={`${usersLen} Users`}>{users}</Collapsible>;
  }

  _handleCommentChange(event) {
    this.setState({ comment: event.target.value });
  }

  renderTable() {
    const { condensed, showDeviceNames, tableData } = this.props;

    return (
      <Table
        data={tableData}
        options={this.options}
        hover={true}
        pagination={true}
      >
        <Column key="id" dataField="id" isKey={true} hidden>
          ID
        </Column>
        <Column dataField="resolve_date" hidden>
          Resolve Date
        </Column>
        <Column dataField="name" dataSort={true} width="180px">
          Name
        </Column>
        <Column
          dataField="create_date"
          dataSort={true}
          dataFormat={formatDate}
          width="180px"
        >
          First Occurrence Date
        </Column>
        <Column
          dataField="last_notified"
          dataSort={true}
          dataFormat={formatDate}
          width="180px"
        >
          Last Notified Date
        </Column>
        {showDeviceNames && (
          <Column
            dataField="device_name"
            dataSort={true}
            dataFormat={formatPrimaryLink}
            formatExtraData={"device_device_id"}
            width="180px"
          >
            Device Name
          </Column>
        )}
        {!condensed && (
          <Column
            dataField="notified"
            dataSort={true}
            dataFormat={this._formatUsers}
            width="30%"
          >
            Notified
          </Column>
        )}
        {!condensed && (
          <Column
            dataField="comment"
            dataFormat={this._formatResolveDetails}
            width="30%"
          >
            Details
          </Column>
        )}
        {!condensed && (
          <Column
            dataField="resolved"
            dataSort={true}
            dataFormat={this._formatIsResolved}
            width="100px"
            dataAlign="center"
          >
            Resolved
          </Column>
        )}
      </Table>
    );
  }

  _submitSuccessHandler() {
    const { resolveMsgHandler } = this.props;
    const message = {
      errorMessage: "",
      successMessage: "Incident resolved.",
    };

    resolveMsgHandler(message);
  }

  _submitErrorHandler(e) {
    const { resolveMsgHandler } = this.props;
    const errorMessage = getErrorMessage(e);
    const message = {
      errorMessage,
      successMessage: "",
    };

    resolveMsgHandler(message);
  }

  _resetModalState() {
    this.setState(this._baseModalState);
  }

  render() {
    const { storesLoaded } = this.props;

    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    return (
      <div className="incidents-table">
        {this.renderTable()}
        {this._renderModal()}
      </div>
    );
  }

  _formSubmit() {
    const { resolveIncident } = this.props;
    const { comment, incidentId } = this.state;
    const data = { comment };
    const serviceArgs = { incidentId, data };

    this._resetModalState();

    return resolveIncident(serviceArgs)
      .then(this._submitSuccessHandler)
      .catch(this._submitErrorHandler);
  }

  _renderModal() {
    const { comment, incidentId } = this.state;

    return (
      <Modal show={incidentId !== null}>
        <Modal.Header>
          <Modal.Title>Resolution Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form>
            <FormGroup controlId="formBasicText">
              <FormLabel>Resolution Comment</FormLabel>
              <FormControl
                type="text"
                value={comment}
                placeholder="Enter resolution comment"
                onChange={this._handleCommentChange}
              />
              <FormControl.Feedback />
              <FormText>
                Only enter resolution details as needed. We will automatically
                record the resolution date and user who made this change.
              </FormText>
            </FormGroup>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this._resetModalState} variant="secondary">
            Cancel
          </Button>
          <Button onClick={this._formSubmit} variant="primary">
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export { IncidentsTable };
